/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { cloneDeep } from 'lodash-es';

import CardBox from '@/pages/dashboard/components/CardBox';
import { useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { formatNum, formatPercentNum } from '@/pages/dashboard/utils/utils';
import { countryList } from '@/pages/dashboard/utils/constant';
import MetricCardList from '@/pages/dashboard/components/MetricCardList';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import { contrastLine, xAxisHighLight, addChartSymbolMulti } from '@/pages/dashboard/components/Chart/_config/plug';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { exportExcel } from '../_config/exportExcel';
import { showDownloadSettings } from './DownloadOptionModal';
import { replacePlaceholders } from '../_config/common';
import { getDataByKey, getTitle, sortDataByTimeAndValue } from '../_utils/common';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import './index.scss';
import { LANGUAGE_EN } from '@home/_config/config';
import { getImg } from '@home/_config/getImgs';
import { dynamicString } from '@home/utils/dynamicString';
import { INT_FORMAT_ARRAY, COLOR_ARR } from '@/pages/dashboard/_config/constant';

const defaultRenderAllData: { 
  chart: any[]; 
  table: any; 
  metricsCardList: any[];
} = {
  chart: [],
  table: [],
  metricsCardList: []
};

interface TrendChartCardProps {
  title: string | React.ReactNode;
  gameCode: string;
  height: number;
  data: {
    componentData: any;
    isError: boolean;
    isLoading: boolean;
  };
  dataBy: string;
  componentMetric: any;
  showDodSdlw?: boolean;
}

const TrendChartCard: React.FC<TrendChartCardProps> = (props) => {
  const { 
    gameCode, 
    data, 
    dataBy, 
    title,
    height,
    componentMetric,
    showDodSdlw
  } = props;
  // 重新定义个dynamicData，用于动态字符串替换
  const dynamicData = data || {};
  const { componentData: metricsData, isError, isLoading } = data || {};
  const { dataType, formatRule: formatRuleStr, showDownloadModal = true, metricsCardList } = componentMetric || {};
  const formatRule = dynamicString(formatRuleStr, dynamicData);
  const { hideTotalIcon = true, totalEn = "Total", totalCn = "总数", showTotal = true } = metricsCardList || {};
  // 保持旧逻辑：dataType 判断 isInt 和 isMoney,增加INT_FORMAT_ARRAY数组判断formatRule兼容旧逻辑
  const isInt = dataType === 'int' || INT_FORMAT_ARRAY?.includes(formatRule);
  const isMoney = dataType === 'money';
  const isEn = getLocale() === LANGUAGE_EN;
  const lineChartRef = useRef<any>();

  // 防止切换国际化时，浮窗不会同步国际化
  useEffect(() => {
    lineChartRef.current?.refreshChart();
  }, [isEn]);

  const { locale, formatMessage } = useIntl();
  const { getFilterItemText } = useSelectorMap();
  const getSelectorText = (by: string, name: string) => {
    return getFilterItemText(by, name, name);
  };
  const isCN = locale === 'zh-CN';

  const [renderData, setRenderData] = useState(defaultRenderAllData);


  const statisticalTime = componentMetric.lastTime?.dataKey ? 
    getDataByKey(componentMetric.lastTime.dataKey, metricsData)?.split(' ')[1] : '';

  const registerDate = componentMetric.registerDate?.dataKey ? 
    getDataByKey(componentMetric.registerDate.dataKey, metricsData) : '';

  // 获取图表配置
  const getChartOptions = (data: any) => data?.map((legendItem: any) => ({
    name: getSelectorText(dataBy, legendItem.name),
    data: legendItem.list?.map((item: any) => ({
      x: item?.time,
      y: item?.value,
      z: item?.share
    }))
  }));

  // 获取表格配置
  const getTableOptions = (data: AnalyserOptions) => {
    const tableConfig = componentMetric?.table?.options || [];
    const placeholders = {
      dataBy
    };
    
    const option = tableConfig.map((item: any) => {
      const newItem = {
        ...item,
        header: getTitle(dynamicString(item.title, dynamicData), dynamicString(item.titleCn, dynamicData), dynamicString(item.formatTitle, dynamicData), locale, formatMessage),
      };
    
      if (item.key === 'time') {
        newItem.render = (cvm: any) => <span>{cvm.time?.split(/\s/)[1]}</span>;
      } else if (item.key === 'type') {
        const dynamicKey = replacePlaceholders(item.formatTitle, placeholders);
        newItem.header = formatMessage({ id: dynamicKey });
        newItem.render = (cvm: any) => <span>{getSelectorText(dataBy, cvm.type) || '-'}</span>;
      } else if (item.key === 'new') {
        newItem.render = (cvm: any) => (
          <span>
            {item?.formatRule ? dataFormat({ value: cvm.new, formatRule: item?.formatRule }) : formatNum(cvm.new, false, true)}
          </span>
        );
      } else if (item.key === 'value') {
        const getFormattedValue = (cvm: any) => {
          // 优先使用列配置的 formatRule
          if (item?.formatRule) {
            return dataFormat({ value: cvm.value, formatRule: item?.formatRule });
          }
          // 其次使用组件的 formatRule
          if (formatRule) {
            return dataFormat({ value: cvm.value, formatRule });
          }
          // 兼容旧逻辑：使用 dataType
          if (isMoney) {
            return formatNum(cvm.value, true, false);
          }
          if (isInt) {
            return formatNum(cvm.value);
          }
          return formatPercentNum(cvm.value);
        };
    
        newItem.render = (cvm: any) => <span>{getFormattedValue(cvm)}</span>;
      } else if (item.key === 'share') {
        newItem.render = (cvm: any) => (
          <span>{dataFormat({ value: cvm.share, formatRule: item?.formatRule || 'percent1Decimal' })}</span>
        );
      }
    
      return newItem;
    });
    const dataList = sortDataByTimeAndValue(data); 
    return { option, data: dataList, };
  };
  // 获取指标列表数据
  const getMetricsCardList = (data: any) => data?.map((item: any) => {
    let formattedValue;
    
    // 优先使用 formatRule（新逻辑）
    if (formatRule) {
      formattedValue = dataFormat({ value: item?.value, formatRule });
      // 如果是货币类型，添加货币符号
      if (isMoney && item?.value) {
        formattedValue = `$${formattedValue}`;
      }
    } else {
      // 兼容旧逻辑：使用 dataType
      if (isInt) {
        formattedValue = formatNum(item?.value, false, true);
      } else if (isMoney) {
        formattedValue = `${item?.value ? '$' : ''}${formatNum(item?.value, true, false)}`;
      } else {
        formattedValue = formatPercentNum(item?.value);
      }
    }

    const isOthers = item?.name?.toLowerCase() === 'others' || item?.name === '其他';
    return {
      name: item?.name === 'Total' ? (isEn ? totalEn : totalCn) : getSelectorText(dataBy, item?.name),
      nameValue: item?.name,
      value: formattedValue,
      ...((!isInt && !isMoney) && { metric: `${formatMessage({ id: 'realtime_by_country_new_tab' })}: ${formatNum(item?.new, false, true)}` }),
      iconName: ['ad_format', 'ua_network']?.includes(dataBy) && !isOthers?  item?.name : dataBy === 'country' && !(item?.name === 'Total' && hideTotalIcon) && !isOthers ? countryList[item?.name] || item?.name : null,
      dod: item?.dod,
      sdlw: item?.sdlw
    };
  });

  
  const updateOption = () => {
    let newRenderData = defaultRenderAllData;
    newRenderData = {
      chart: getChartOptions(getDataByKey(componentMetric?.chart?.dataKey, metricsData)),
      table: getTableOptions(getDataByKey(componentMetric?.table?.dataKey, metricsData)),
      metricsCardList: getMetricsCardList(getDataByKey(componentMetric?.metricsCardList?.dataKey, metricsData))
    };

    setRenderData(newRenderData);
  };

  useEffect(() => {
    if (!metricsData) return;
    updateOption();
  }, [data, locale]);

  const lineFormatterFunc = (param: any, title: any, isMoney = false, isPercent = false, isFloating = false, showShare = false, shareTitle: string, shareFormat: string, valueFormatRule?: string) => {
    const shareTitleView = showShare ? `<p class='basic-formatter-two basic-formatter-title'>${shareTitle}</p>` : ``;
    let returnStr = `<div class=${'basic-formatter-box'}>
            <div class=${'basic-formatter-header'}>
            <p class='basic-formatter-one basic-formatter-title'>${param[0].axisValue}</p>
            <p class='basic-formatter-two basic-formatter-title'>${title}</p>
            ${shareTitleView}
            </div>`;
    
    for (const item of param) {
      const { componentSubType, seriesType, data, seriesName, marker } = item;
      const value = data?.value || '-';
      
      // 优先使用 valueFormatRule（新逻辑）
      let dauValue;
      if (valueFormatRule) {
        dauValue = dataFormat({ value, formatRule: valueFormatRule });
      } else {
        // 兼容旧逻辑：根据类型判断
        if (isMoney) {
          dauValue = formatNum(value, true, false);
        } else if (isPercent) {
          dauValue = formatPercentNum(value, 2, false);
        } else {
          dauValue = formatNum(value, false, !isFloating);
        }
      }
      
      const shareValue = dataFormat({
        value: data?.share,
        formatRule: shareFormat || 'percent1Decimal'
      });
      const shareView = showShare ? `<p class="basic-formatter-two">${shareValue}</p>` : ``;
      // realtime最新值动效类型特殊处理
      if (componentSubType === 'effectScatter' && seriesType === 'effectScatter') {
        break;
      }

      returnStr += `
        <div class="basic-formatter-header">
          <p class="basic-formatter-one">${marker}&nbsp;${seriesName}</p>
          <p class="basic-formatter-two">${dauValue}</p>
          ${shareView}
        </div>`;
    }

    returnStr = `${returnStr}`;
    return returnStr;
  };

  const tabsEchart = (option: any) => {
    const xAxisData = option?.chart?.[0]?.data?.map((item: any) => item?.x) || [];
    const legendData = option?.chart?.map((item: any) => item?.name) || [];
    const series = option?.chart?.map((item: any, idx: number) => ({
      name: item?.name,
      type: 'line',
      color: COLOR_ARR[idx % COLOR_ARR.length],
      data: item?.data?.map((cItem: any) => ({
        value: cItem?.y,
        share: cItem?.z,
        hideCircle: true
      }))
    })) || [];
    return [
      {
        type: 'lineChart',
        svgType: 'discount-chart',
        content: formatMessage({ id: "common_line_chart" }),
        component: (
          <LineChart
            ref={lineChartRef}
            ripples
            multiLine
            height={350}
            legendData={xAxisData.length > 0 ? legendData : []}
            xAxisData={xAxisData || []}
            series={xAxisData.length > 0 ? series : []}
            formatterFunc={(e: any) => {
              const tooltipConfig = componentMetric.chart.tooltip;
              const { title: tooltipTitleEn, titleCn: tooltipTitleCn, formatTitle: tooltipFormatTitle } = tooltipConfig || {};
              const { title: componentTitleEn, titleCn: componentTitleCn, formatTitle: componentFormatTitle } = componentMetric.title || {};
              const showShare = tooltipConfig?.share;
              const title = tooltipTitleEn && tooltipTitleCn ?
                getTitle(dynamicString(tooltipTitleEn, dynamicData), dynamicString(tooltipTitleCn, dynamicData), dynamicString(tooltipFormatTitle, dynamicData), locale, formatMessage) :
                getTitle(dynamicString(componentTitleEn, dynamicData), dynamicString(componentTitleCn, dynamicData), dynamicString(componentFormatTitle, dynamicData), locale, formatMessage);
              const shareTitle = getTitle(dynamicString(tooltipConfig?.shareTitle, dynamicData), dynamicString(tooltipConfig?.shareTitleCn, dynamicData), 'common_share', locale, formatMessage);
              const shareFormat = tooltipConfig?.shareFormat;
              // 优先使用 tooltip 的 formatRule，否则使用组件的 formatRule
              const valueFormatRule = tooltipConfig?.formatRule || formatRule;
              return lineFormatterFunc(e, title, isMoney, !isInt, false, showShare, shareTitle, shareFormat, valueFormatRule);
            }}
            setOtherConfigFunc={(opt: any) => {
              opt.grid.x2 = 15;
              opt = addChartSymbolMulti(opt, {
                showList: ['new', 'max'],
                formatter: (params: any) => {
                  if (formatRule) {
                    const formattedValue = dataFormat({ value: params?.value, formatRule });
                    if (isMoney && params?.value) {
                      return `$${formattedValue}`;
                    }
                    return formattedValue;
                  }
                  if (!isInt && !isMoney) {
                    return formatPercentNum(params?.value, 2, false);
                  }
                  return formatNum(params?.value, false, true);
                },
              });
              opt = contrastLine(opt, {
                formatter: (value: any) => value ? value?.slice(11, 16) : '',
                isAllSolid: true
              });
              if (!isInt && !isMoney && !["thousands2Decimal"]?.includes(formatRule)) {
                opt.yAxis.axisLabel.formatter = (value: any) => yAxisFormat(value, true, 0);
              }
              opt = xAxisHighLight(opt, {
                formatter: (params: any) => params.value ? params.value?.slice(11, 16) : '',
                isRealtime: true
              });
              return opt;
            }}
          />
        ),
      },
      {
        type: 'sheet',
        svgType: 'sheet',
        content: formatMessage({ id: "common_table" }),
        component: (<MiniTable
          height={350}
          loading={false}
          tableOption={option?.table?.option}
          tableConfig={{ isSortable: true, columns: ['time'], defaultValue: [{ by: 'time', order: 'desc' }] }}
          tableList={option?.table?.data}
          scrolLableVal={{
            virtualizedOptions: {
              height: 296,
              itemHeight: 50,
            },
          }}
        />),
      }
    ];
  };

  const downloadOptions: any = {
    formatMessage,
    // 克隆，避免后续改 type.disabled 污染 componentMetric.download
    downloadConfig: Array.isArray(componentMetric?.download)
      ? cloneDeep(componentMetric.download)
      : [],
    gameCode,
    isCN,
  };

  const hasNoData = (options: any) => {
    if (!options?.chart || options?.chart?.length <= 0) {
      return true; // 趋势图没数据
    }
  
    const tableData = options?.table?.data || [];
  
    if (isInt||isMoney ) { // 宽表处理逻辑不同
      return tableData.length === 0 || tableData.every((item: any) => !item?.value);
    } 
    return tableData.every((item: any) => !item || item.value === '');
  };
  
  const openDownloadSetting = async (name: any, table: any) => {
    // 仅在存在可用的时间粒度选项时弹出下载设置；缺少 download 配置时直接下载，避免读 type 报错
    const typeOptions = downloadOptions?.downloadConfig?.[0]?.type;
    if (showDownloadModal && typeOptions?.length >= 1) {
      const {data} = table;
      const lhour: number = data.length ? parseInt(data?.[data.length - 1].time?.split(':')?.[0]) : 0;
  
      const disabledType = typeOptions.find((item: any) => item.value === '60');
      if (disabledType) {
        disabledType.disabled = lhour < 1;
      }
      
      try {
        const val = await showDownloadSettings(downloadOptions);
        handleDownload(name, table, val);
      } catch (error) {
        console.log(error);
      }
    } else {
      handleDownload(name, table);
    }
  };
  const handleDownload = (name: any, table: any, downloadOptions: any = { timeScale: '60' }) => {
    // 避免 JSON.stringify 将 Infinity/NaN 转为 null，导致占比极大值下载成 '-'
    const newTable = cloneDeep(table);
    // Process table data based on isInt condition
    if ((isInt||isMoney) && showDownloadModal ) {
      const DEFAULT_TIME_SCALE = 5; // 接口数据的默认时间间隔
      const DEFAULT_SORT = 1;
      const timeScale: number = parseInt(downloadOptions.timeScale);
      const {sort} = downloadOptions;
  
      // Filter table data based on timeScale
      const filterDataByTimeScale = (item: any) => {
        if (timeScale === DEFAULT_TIME_SCALE) return true;
        const min: number = parseInt(item?.time?.split(':')?.[1]);
        const hour: number = parseInt(item?.time?.split(':')?.[0]);
        let time: number = hour * 60 + min;
        if (time === 23 * 60 + 59) {  // 把最后一刻的时间取整
          time = 24 * 60;
        }
        return time % timeScale === 0;
      };
      newTable.data = newTable?.data?.filter(filterDataByTimeScale);
      // Common data processing
      processData(newTable.data, dataBy);
      // Reverse data if sort is not default
      if (sort !== DEFAULT_SORT) {
        sortDataByTime(newTable.data);
      }
    } else {
      // Process non-integer table data
      processData(newTable.data, dataBy);
  
      // Sort data by time in descending order
      sortDataByTime(newTable.data);
    }
    // 60分钟时间粒度名称特殊处理
    if (parseInt(downloadOptions.timeScale) === 60) {
      name = isEn ? name?.replace('5 min', '1 hour') : name?.replace('5 分钟', '1 小时');
    }
    // Export Excel
    exportExcel(name, newTable.data, newTable.option);
    return true;
  };
  
  // Helper function to process the data
  const processData = (data: any[], dataBy: any) => {
    data.forEach((entry: any) => {
      entry.time = entry.time?.split(/\s/)[1];
      entry.type = getSelectorText(dataBy, entry.type) || '-';
    });
  };
  
  // Helper function to sort data by time in descending order
  const sortDataByTime = (data: any[]) => {
    data.sort((a: any, b: any) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return timeB[0] - timeA[0] || timeB[1] - timeA[1];
    });
  };
  
  const titleCom = useMemo(() => (
    <>
      {
        componentMetric?.preTitleIcon && (
          <img className='dbd-realtime-icon-pre-img' src={getImg(componentMetric?.preTitleIcon || "")} />
        )
      }
      <p>
        {title}
      </p>
      {
        componentMetric?.titleIcon && (
          <img className='dbd-realtime-icon-img' src={getImg(componentMetric?.titleIcon || "")} />
        )
      }
    </>
  ), [componentMetric, title]);
  
  return <>
    <CardBox
      className='dbd-realtime-country-trend margin-t-20 table-line'
      titleCom={titleCom}
      bordered={true}
      height={height || 478}
      middleContentCom={
        !isLoading && <MetricCardList 
          list={renderData.metricsCardList} 
          showDodSdlw={showDodSdlw}
          showTotal={showTotal}
          dataBy={dataBy}
          { ...(statisticalTime && { time: statisticalTime }) }
          { ...(registerDate && { registerDate }) }
        />
      }         
      renderList={tabsEchart(renderData)}
      downloadClick={async () => {
        openDownloadSetting(title, renderData.table);
      }}
      isLoading={isLoading}
      isError={isError}
      isNodata={hasNoData(renderData)}
      failedMsg={formatMessage({ id: 'common_loading_failed_realtime'})}
    />
  </>;
};

export default TrendChartCard;